import { useCallback, useState } from "react";

import { coverageMovementStorage } from "../../../lib/storage";
import type { CoveragePlan, Movement } from "../read/readCoverage";

/** Membership id → the newest movement the user has opened. */
type Seen = Record<string, string>;

const SEEN_KEY = "seen";

const readSeen = (): Seen => {
  try {
    const parsed: unknown = JSON.parse(
      coverageMovementStorage.get(SEEN_KEY) ?? "{}",
    );
    return typeof parsed === "object" && parsed !== null
      ? Object.fromEntries(
          Object.entries(parsed).filter(
            (entry): entry is [string, string] => typeof entry[1] === "string",
          ),
        )
      : {};
  } catch {
    return {};
  }
};

export const clearSeenMovements = () =>
  coverageMovementStorage.remove(SEEN_KEY);

/** The plan's lead movement until the user opens it; a newer one shows again. */
export function useMovementNotice({
  membershipId,
  movement,
}: Pick<CoveragePlan, "membershipId" | "movement">): {
  notice: Movement | null;
  markSeen: () => void;
} {
  const [seen, setSeen] = useState(readSeen);
  const at = movement?.at.toISOString() ?? null;
  const markSeen = useCallback(() => {
    if (at) {
      const next = { ...readSeen(), [membershipId]: at };
      coverageMovementStorage.set(SEEN_KEY, JSON.stringify(next));
      setSeen(next);
    }
  }, [membershipId, at]);
  const seenAt = seen[membershipId];
  const unseen = at !== null && (seenAt === undefined || seenAt < at);
  return { notice: unseen ? movement : null, markSeen };
}
